// Affiliates.jsx - The people who send us customers, what they have sent, and
// what they are owed.
//
// Adding one is deliberately a single field. Somebody standing at a show with a
// jeweller in front of them types a name, presses one button, and gets a code
// and a link to hand over. Everything else about that person can be filled in
// later from the same screen, because a form that asks for an address before it
// will save anything is a form that gets a fake address typed into it.
//
// The code is generated unless somebody wants a particular one. A vanity code is
// the only reason to type in that box, and the box says so.
//
// LEGEND
//   1. AffiliateDialog  add one, or edit one, in the same form
//   2. Application      one waiting to be approved, with everything they said
//   3. Row              one affiliate, expanding to their orders
//   4. Affiliates       the page

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import clsx from "clsx";

import { useAuth } from "./AdminApp.jsx";
import { siteOrigin } from "../lib/host.js";
import { useAsync, useDebounced, useForm } from "../lib/hooks.js";
import { affiliatesApi } from "../lib/api.js";
import { money, ago, day, phone as formatPhone } from "../lib/format.js";
import {
  Panel, Input, Textarea, Loading, Alert, Empty, Tag, Button, Modal, Toggle, CopyButton, useToast,
} from "../ui/kit.jsx";
import { FormAddressField } from "../ui/address.jsx";
import { Loupe, Plus, Users, Trash, ChevronDown, Dollar, Check, Printer } from "../ui/icons.jsx";

////////// 1. ADD OR EDIT //////////

function AffiliateDialog({ open, affiliate, commission, onClose, onSaved }) {
  const toast = useToast();
  const editing = Boolean(affiliate);
  const [more, setMore] = useState(false);

  const form = useForm({
    name: "", code: "", company: "", email: "", phone: "",
    address: { line1: "", line2: "", city: "", state: "", postal: "", country: "US" },
    payoutMethod: "", payoutNote: "", notes: "", status: "active",
  });

  // Reset every time it opens, so editing one person and then adding another
  // does not start with the first person's details.
  useEffect(() => {
    if (!open) return;
    setMore(false);
    form.setValues(affiliate
      ? {
          name: affiliate.name || "", code: affiliate.code || "", company: affiliate.company || "",
          email: affiliate.email || "", phone: affiliate.phone || "",
          address: { line1: "", line2: "", city: "", state: "", postal: "", country: "US", ...(affiliate.address || {}) },
          payoutMethod: affiliate.payoutMethod || "", payoutNote: affiliate.payoutNote || "",
          notes: affiliate.notes || "", status: affiliate.status || "active",
        }
      : {
          name: "", code: "", company: "", email: "", phone: "",
          address: { line1: "", line2: "", city: "", state: "", postal: "", country: "US" },
          payoutMethod: "", payoutNote: "", notes: "", status: "active",
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, affiliate?.code]);

  const submit = form.submit(async (values) => {
    const saved = editing
      ? await affiliatesApi.update(affiliate.code, values)
      : await affiliatesApi.create(values);
    toast.success(editing ? "Saved" : `${saved.name} is ${saved.code}`);
    onSaved(saved);
    onClose();
    return saved;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${affiliate.name}` : "Add an affiliate"}
      description={editing
        ? `Code ${affiliate.code}. The code cannot change: it is on their link and on every order they have sent.`
        : "A name is enough. Everything else can be filled in later."}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={form.busy}>{editing ? "Save" : "Add and make a code"}</Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input label="Name" required autoFocus placeholder="Dave Whitmore" {...form.field("name")} />

        {!editing && (
          <Input
            label="Their own code"
            placeholder="Made up for you"
            hint="Only if they want a particular one. Short, letters and numbers."
            {...form.field("code")}
          />
        )}

        {editing && (
          <Toggle
            label={form.values.status === "active" ? "Taking new orders" : "Retired"}
            hint="Retired stops their code attaching to anything new. The orders they already sent keep their name and their commission."
            checked={form.values.status === "active"}
            onChange={(value) => form.set("status", value ? "active" : "retired")}
          />
        )}

        {/* Everything below is optional, and stays out of the way until it is
            wanted. The dialog is one field tall for the case it is used for
            most: somebody being added at a show. */}
        <button
          type="button"
          onClick={() => setMore(!more)}
          className="flex items-center gap-1.5 text-[13px] text-muted hover:text-ink transition-colors"
        >
          <ChevronDown size={15} className={clsx("transition-transform", more && "rotate-180")} />
          {more ? "Fewer details" : "Add their details"}
        </button>

        {more && (
          <div className="space-y-4 pt-1 fade">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Company" {...form.field("company")} />
              <Input label="Email" type="email" {...form.field("email")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Phone" type="tel" {...form.field("phone")} />
              <Input label="How we pay them" placeholder="Check, Zelle" {...form.field("payoutMethod")} />
            </div>
            <Input label="Where to send it" placeholder="Zelle number, address for a check" {...form.field("payoutNote")} />
            <FormAddressField form={form} label="Street" />
            <div className="grid grid-cols-3 gap-4">
              <Input label="City" {...form.field("address.city")} />
              <Input label="State" maxLength={2} {...form.field("address.state")} />
              <Input label="ZIP" {...form.field("address.postal")} />
            </div>
            <Textarea label="Notes" rows={3} placeholder="Where you met, what they do, what was agreed." {...form.field("notes")} />
          </div>
        )}

        {!editing && commission && (
          <p className="hint leading-relaxed pt-1 border-t border-rule-soft">
            They will earn {commission.commissionPercent}% of what each customer they send is paid
            {commission.commissionCap > 0 ? `, up to ${money(commission.commissionCap)} per order` : ""}.
            Those terms are copied onto every order when it comes in, so changing them later never
            changes what is already owed.
          </p>
        )}

        {form.message && <Alert tone="error">{form.message.text}</Alert>}
      </form>
    </Modal>
  );
}

////////// 2. AN APPLICATION //////////
// Applications sit above the list, not in it. Somebody is waiting for an answer
// and an application that sits for a week is a partner who went somewhere else,
// so this is a queue with two buttons on it rather than a row that has to be
// noticed.
//
// Everything they told us is on screen. Approving is a decision, and a decision
// made from a summary is a decision made from a guess.

function Application({ affiliate, onDecided }) {
  const toast = useToast();
  const [busy, setBusy] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");

  const applied = affiliate.applied || {};

  const decide = async (decision) => {
    setBusy(decision);
    try {
      const result = await affiliatesApi.decide(affiliate.code, { decision, reason: reason || undefined });
      toast.success(decision === "approve"
        ? `${result.name} is set up as ${result.code}${result.emailed ? " and has been emailed" : ""}`
        : `${result.name} was turned down`);
      setDeclining(false);
      onDecided();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className="border-b border-rule-soft last:border-0 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-ink">
            {affiliate.name}
            {affiliate.company ? <span className="text-muted font-normal"> at {affiliate.company}</span> : null}
          </p>
          <p className="hint num mt-0.5">
            {affiliate.email}{affiliate.phone ? ` · ${formatPhone(affiliate.phone)}` : ""}
          </p>
        </div>
        <span className="hint shrink-0">Applied {ago(applied.at || affiliate.createdAt)}</span>
      </div>

      <dl className="grid sm:grid-cols-2 gap-x-6 text-[13px] mt-3">
        {[
          ["What they do", applied.businessType],
          ["Premises", applied.hasPremises
            ? [affiliate.address?.city, affiliate.address?.state].filter(Boolean).join(", ") || "Yes"
            : "None, online only"],
          ["Wants a display", applied.hasPremises ? (applied.wantsDisplay ? "Yes" : "No") : "Not applicable"],
          ["People a month", applied.customersPerMonth],
          ["Website", applied.website],
          ["Wants to be paid by", affiliate.payoutMethod],
          ["Heard about us", applied.heardAbout],
        ].filter(([, value]) => value).map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-1 border-b border-rule-soft">
            <dt className="text-muted shrink-0">{label}</dt>
            <dd className="text-ink text-right min-w-0 break-words">{value}</dd>
          </div>
        ))}
      </dl>

      <p className="hint mt-3">
        Agreed to {(affiliate.agreements || []).map((row) => `${row.title} v${row.version}`).join(", ") || "nothing recorded"}.
      </p>

      {declining ? (
        <div className="mt-4 space-y-3 fade">
          <Input
            label="Why, for our own record"
            placeholder="Optional. They are never sent this."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="danger" loading={busy === "decline"} onClick={() => decide("decline")}>
              Turn them down
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setDeclining(false)}>Cancel</Button>
          </div>
          <p className="hint">
            Nothing is emailed. Turning somebody down is a call, not a form letter, and their record
            is kept so nobody wonders later.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" loading={busy === "approve"} onClick={() => decide("approve")}>
            <Check size={15} /> Approve and send their code
          </Button>
          <Button size="sm" variant="quiet" onClick={() => setDeclining(true)}>Turn down</Button>
          {affiliate.phone && (
            <a href={`tel:${affiliate.phone}`} className="btn btn-quiet btn-sm">Call first</a>
          )}
        </div>
      )}
    </li>
  );
}

////////// 3. ONE AFFILIATE //////////

function Row({ affiliate, onChanged, onEdit }) {
  const { can } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);

  const { data, loading, reload } = useAsync(
    () => (open ? affiliatesApi.get(affiliate.code) : Promise.resolve(null)),
    [open, affiliate.code]
  );

  const totals = affiliate.totals || {};
  const unpaid = (data?.orders?.items || []).filter(
    (order) => order.commission?.amount > 0 && !order.commission?.paidAt
  );

  const markPaid = async () => {
    setBusy(true);
    try {
      const result = await affiliatesApi.markPaid(affiliate.code, { numbers: unpaid.map((order) => order.number) });
      toast.success(`Marked ${result.marked} ${result.marked === 1 ? "order" : "orders"} as paid`);
      reload();
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await affiliatesApi.remove(affiliate.code);
      toast.success(`Removed ${affiliate.name}`);
      setRemoving(false);
      onChanged();
    } catch (error) {
      toast.error(error.message);
      setRemoving(false);
    }
  };

  return (
    <li className={clsx("border-b border-rule-soft last:border-0", !affiliate.active && "opacity-60")}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
          aria-expanded={open}
        >
          <ChevronDown size={15} className={clsx("shrink-0 text-faint transition-transform", open && "rotate-180")} />
          <span className="min-w-0">
            <span className="flex items-center gap-2 flex-wrap">
              <span className="text-[14.5px] font-medium text-ink">{affiliate.name}</span>
              <span className="tag tag-bronze num">{affiliate.code}</span>
              {affiliate.status === "retired" && <Tag tone="neutral">Retired</Tag>}
              {affiliate.status === "declined" && <Tag tone="critical">Turned down</Tag>}
            </span>
            <span className="block hint num truncate">
              {affiliate.company || affiliate.email || (affiliate.phone ? formatPhone(affiliate.phone) : "No details yet")}
            </span>
          </span>
        </button>

        <span className="text-right shrink-0">
          <span className="block text-[13px] text-ink num">
            {totals.orders || 0} {totals.orders === 1 ? "order" : "orders"}
          </span>
          <span className={clsx("block text-[12px] num", totals.owed > 0 ? "text-critical font-semibold" : "text-faint")}>
            {totals.owed > 0 ? `${money(totals.owed)} owed` : "nothing owed"}
          </span>
        </span>

        <CopyButton value={affiliate.link} label="Copy link" className="shrink-0" />
      </div>

      {open && (
        <div className="px-4 pb-4 pt-1 fade">
          <div className="grid gap-4 lg:grid-cols-[1fr_1fr] lg:gap-6">
            <div>
              <p className="eyebrow mb-2">Their link</p>
              <p className="num text-[13px] text-ink break-all bg-surface px-3 py-2 border border-rule-soft">
                {affiliate.link}
              </p>
              <p className="hint mt-2 leading-relaxed">
                Anyone who fills the kit form in after following this is credited to them for ninety
                days, whether or not they finish it the same afternoon.
              </p>

              {/* The paper half of the same thing. A shop counter has no link on
                  it, so the code goes on a card in the mailer instead. */}
              <Link
                to={`/admin/affiliates/cards?code=${affiliate.code}`}
                className="btn btn-quiet btn-sm mt-3"
              >
                <Printer size={14} /> Print counter cards
              </Link>
              <p className="hint mt-2 leading-relaxed">
                One for each display mailer. The form they fill in by hand, and a QR code on the back
                that registers them before they post, credited to this code.
              </p>
              <p className="hint mt-2 leading-relaxed">
                Send them the{" "}
                <a href="/legal/affiliate-terms" target="_blank" rel="noopener noreferrer" className="link text-ink">
                  affiliate terms
                </a>. That page is not linked from anywhere a customer looks.
              </p>

              <dl className="text-[13px] divide-y divide-rule-soft mt-4">
                {[
                  ["Sent", `${totals.orders || 0} orders, ${totals.open || 0} still open`],
                  ["Earned so far", money(totals.earned || 0)],
                  ["Paid", money(totals.paid || 0)],
                  ["Owed", money(totals.owed || 0)],
                  ["Last order", totals.lastAt ? ago(totals.lastAt) : "None yet"],
                  ["Added", `${day(affiliate.createdAt)}${affiliate.addedByName ? ` by ${affiliate.addedByName}` : ""}`],
                  ...(affiliate.agreedTerms?.version
                    ? [["Terms given", `v${affiliate.agreedTerms.version}`]]
                    : []),
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 py-1.5">
                    <dt className="text-muted">{label}</dt>
                    <dd className="text-ink text-right num">{value}</dd>
                  </div>
                ))}
              </dl>

              {affiliate.payoutMethod && (
                <p className="hint mt-3 leading-relaxed">
                  Paid by {affiliate.payoutMethod}{affiliate.payoutNote ? `, ${affiliate.payoutNote}` : ""}.
                </p>
              )}
              {affiliate.notes && <p className="hint mt-2 leading-relaxed whitespace-pre-line">{affiliate.notes}</p>}

              <div className="flex flex-wrap gap-2 mt-4">
                {can("affiliates.write") && (
                  <Button size="sm" variant="quiet" onClick={() => onEdit(affiliate)}>Edit</Button>
                )}
                {can("affiliates.write") && unpaid.length > 0 && (
                  <Button size="sm" onClick={markPaid} loading={busy}>
                    <Dollar size={15} /> Mark {money(unpaid.reduce((sum, order) => sum + order.commission.amount, 0))} paid
                  </Button>
                )}
                {can("affiliates.write") && (totals.orders || 0) === 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setRemoving(true)} aria-label={`Remove ${affiliate.name}`}>
                    <Trash size={15} />
                  </Button>
                )}
              </div>
            </div>

            <div>
              <p className="eyebrow mb-2">Orders they sent</p>
              {loading ? (
                <Loading />
              ) : !data?.orders?.items?.length ? (
                <p className="hint">Nothing yet.</p>
              ) : (
                <ul className="divide-y divide-rule-soft border border-rule-soft">
                  {data.orders.items.map((order) => (
                    <li key={order.number} className="px-3 py-2 flex items-center justify-between gap-3">
                      <Link to={`/admin/orders/${order.number}`} className="min-w-0">
                        <span className="block text-[13px] text-ink num">{order.number}</span>
                        <span className="block text-[12px] text-faint truncate">{order.name || "No name"} &middot; {order.statusLabel}</span>
                      </Link>
                      <span className="text-right shrink-0">
                        <span className="block text-[13px] num text-ink">
                          {order.commission?.amount > 0 ? money(order.commission.amount) : "—"}
                        </span>
                        {order.commission?.paidAt
                          ? <span className="text-[11px] text-positive font-semibold">Paid</span>
                          : order.commission?.amount > 0
                            ? <span className="text-[11px] text-critical font-semibold">Owed</span>
                            : <span className="text-[11px] text-faint">Not paid out yet</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {data?.orders?.total > data?.orders?.items?.length && (
                <p className="hint mt-2 num">Showing {data.orders.items.length} of {data.orders.total}.</p>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        open={removing}
        onClose={() => setRemoving(false)}
        title={`Remove ${affiliate.name}?`}
        description="They have sent nothing, so there is nothing to lose."
        footer={<><Button variant="quiet" onClick={() => setRemoving(false)}>Cancel</Button><Button variant="danger" onClick={remove}>Remove</Button></>}
      >
        <Alert tone="warning">
          Their code stops working. If they ever send an order, retire them instead so their name
          stays on it.
        </Alert>
      </Modal>
    </li>
  );
}

////////// 4. THE PAGE //////////

export default function Affiliates() {
  const { can } = useAuth();
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const debounced = useDebounced(search, 300);

  const { data, loading, error, reload } = useAsync(
    (signal) => affiliatesApi.list({ q: debounced || undefined }, signal),
    [debounced]
  );

  const all = data?.affiliates || [];
  const waiting = all.filter((entry) => entry.status === "pending");
  const rest = all.filter((entry) => entry.status !== "pending");
  const owed = rest.reduce((sum, entry) => sum + (entry.totals?.owed || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="display display-lg text-ink">Affiliates</h1>
          <p className="hint mt-1 measure leading-relaxed">
            People who send us customers. Each has a code and a link, and every order that arrives
            through one carries their name and the commission terms of the day it came in.
          </p>
        </div>
        {can("affiliates.write") && (
          <Button onClick={() => setAdding(true)} className="w-full xs:w-auto">
            <Plus size={16} /> Add an affiliate
          </Button>
        )}
      </header>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="sm:w-80 shrink-0">
          <Input
            placeholder="Name or code"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            prefix={<Loupe size={16} />}
            className="pl-9"
            aria-label="Search affiliates"
          />
        </div>
        {owed > 0 && (
          <p className="hint self-center num">
            <span className="text-critical font-semibold">{money(owed)}</span> owed across everybody.
          </p>
        )}
      </div>

      {error && <Alert tone="error" className="mb-4">{error.message}</Alert>}

      {/* Above the list, always, and impossible to walk past. Somebody is on the
          other end of each of these waiting for an answer. */}
      {waiting.length > 0 && (
        <Panel className="overflow-hidden mb-6 border-bronze">
          <div className="px-4 py-3 bg-bronze-wash border-b border-bronze/30">
            <p className="text-[14px] font-semibold text-bronze-dark">
              {waiting.length} {waiting.length === 1 ? "application" : "applications"} waiting
            </p>
            <p className="hint mt-0.5">
              Nothing they were given works until one of these is approved.
            </p>
          </div>
          <ul>
            {waiting.map((affiliate) => (
              <Application key={affiliate.code} affiliate={affiliate} onDecided={reload} />
            ))}
          </ul>
        </Panel>
      )}

      <Panel className="overflow-hidden">
        {loading ? (
          <Loading />
        ) : !rest.length ? (
          <Empty
            title={debounced ? "Nobody matched that" : "No affiliates yet"}
            icon={Users}
            action={can("affiliates.write") && !debounced
              ? <Button onClick={() => setAdding(true)}><Plus size={16} /> Add the first one</Button>
              : null}
          >
            {debounced
              ? `Nothing matches "${debounced}".`
              : waiting.length
                ? "Approve one of the applications above and they appear here."
                : "Add somebody and they get a code and a link straight away."}
          </Empty>
        ) : (
          <ul>
            {rest.map((affiliate) => (
              <Row key={affiliate.code} affiliate={affiliate} onChanged={reload} onEdit={setEditing} />
            ))}
          </ul>
        )}
      </Panel>

      <Panel quiet className="p-4 mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="eyebrow">The application link</p>
          <p className="hint mt-1 leading-relaxed">
            Send this to somebody you have spoken to. It is not linked from the site and search
            engines are told to ignore it, so the only way to it is you sending it.
          </p>
        </div>
        <CopyButton value={`${siteOrigin()}/affiliate-signup`} label="Copy the link" className="shrink-0" />
      </Panel>

      {data?.commission && (
        <p className="hint mt-4 leading-relaxed measure-wide">
          Everybody earns {data.commission.commissionPercent}% of what each customer they send is paid
          {data.commission.commissionCap > 0 ? `, up to ${money(data.commission.commissionCap)} on one order` : ""}.
          Change that under <Link to="/admin/settings?tab=pricing" className="link text-ink">Settings, Pricing</Link>.
          Orders that already exist keep the terms they were created with.
        </p>
      )}

      <AffiliateDialog
        open={adding || Boolean(editing)}
        affiliate={editing}
        commission={data?.commission}
        onClose={() => { setAdding(false); setEditing(null); }}
        onSaved={reload}
      />
    </div>
  );
}
